import { Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { UserRole } from '@eppendorf/vnls-user-tenant-utils';
import type { FunctionComponent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUserId } from '$shared/custom-hooks/useCurrentUserId';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { useGetUser } from '$features/users/users.api';

import './notification-detail-footer.scss';

export interface NotificationDetailFooterProps {
  onEditDialogOpen: () => void;
  onArchiveNotificationRule: () => void;
  canArchive: boolean;
  createdBy: string;
}

export function NotificationDetailFooter({
  onEditDialogOpen,
  onArchiveNotificationRule,
  canArchive,
  createdBy,
}: NotificationDetailFooterProps): ReactElement<
  NotificationDetailFooterProps,
  FunctionComponent<NotificationDetailFooterProps>
> | null {
  const { t } = useTranslation();
  const { email: emailFromToken, tenantId } = useTokenAttributes(['email', 'tenantId']);
  const currentUserId = useCurrentUserId(emailFromToken, tenantId);
  const { data: user } = useGetUser(currentUserId || '');
  const isLabMember = user?.roles?.length === 1 && user.roles[0] === UserRole.LabMember;
  const isRestrictedLabMemberView = isLabMember && createdBy !== user?.email;

  if (isRestrictedLabMemberView) return null;

  if (!canArchive) {
    return (
      <div className="m-top-xxl flex flex__jc--flex-end">
        <Button size="small" onClick={onEditDialogOpen}>
          {t('shared.edit')}
        </Button>
      </div>
    );
  }

  return (
    <div className="m-top-xxl flex flex__jc--space-between w-full notification-detail-footer">
      <Button
        className="notification-detail-footer__delete-button"
        leadingIcon={
          <Icon name="delete" size={IconSizeClasses.XSmall} className="bg-red-500" />
        }
        variant="tertiary"
        color="danger"
        size="small"
        onClick={onArchiveNotificationRule}
      >
        {t('shared.delete')}
      </Button>
      <Button size="small" onClick={onEditDialogOpen}>
        {t('shared.edit')}
      </Button>
    </div>
  );
}
